/**
 * AP-48 — Inhalt des Bautagesberichts im ERP-Entwurf: wie die Speicherstelle ihn führt und wie ein
 * Feldpatch (`geraete.0.anzahl`) in den ganzen Inhalt kommt. Gespeichert wird immer der Inhalt als Ganzes
 * (`PATCH /site-docs/diary/:id` mit `{ content }`) — der Server prüft und schreibt die Behinderungen in
 * ihre Tabelle (AP-48 10.).
 */
import { tagebuchAusLetztem, tagebuchInhalt, type TagebuchInhalt, type TagebuchVorbelegung } from '@symphenia/shared-types'
import type { Datensatz } from '@/lib/speichern'

/** Die Teile des Inhalts — so heißen auch die obersten Felder der Speicherstelle. */
export const TAGEBUCH_TEILE = ['arbeitszeit', 'wetter', 'stillstand', 'belegschaft', 'geraete', 'leistungen', 'lieferungen', 'behinderungen', 'anordnungen', 'vorkommnisse', 'bemerkung'] as const

const istObjekt = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === 'object' && !Array.isArray(v)

/**
 * Antwort des Servers → Datensatz der Speicherstelle: der Inhalt, **nur mit den Teilen, die die Antwort
 * trägt**, dazu `updatedAt`. Die Antwort eines Konflikts (409) ist der rohe Datensatz ohne Behinderungen —
 * fehlt ein Teil, behält die Stelle ihre Basis, statt ihn für leer zu halten.
 */
export function tagebuchDatensatz(antwort: unknown): Datensatz {
  const a = (antwort ?? {}) as { content?: unknown; updatedAt?: unknown }
  const aus: Datensatz = {}
  if (istObjekt(a.content)) {
    const c = a.content
    const voll = tagebuchInhalt(c)
    for (const teil of TAGEBUCH_TEILE) if (teil in c) aus[teil] = voll[teil]
  }
  if (typeof a.updatedAt === 'string') aus.updatedAt = a.updatedAt
  else if (a.updatedAt instanceof Date) aus.updatedAt = a.updatedAt.toISOString()
  return aus
}

/** Angefasst: die Marken `vorbelegt` und `fortgeschrieben` fallen weg; wer die Art wählt, bestätigt sie. */
function angefasst(element: unknown, patch: unknown): unknown {
  if (!istObjekt(element)) return element
  const { vorbelegt: _v, fortgeschrieben: _f, ...rest } = element
  if (istObjekt(patch) && 'art' in patch) delete rest.artVorschlag
  return rest
}

/**
 * Einen Feldpatch in den Inhalt einsetzen. `useFeld` baut aus `geraete.0.anzahl` ein Objekt
 * `{ geraete: { 0: { anzahl } } }`; an einer Liste wird daraus das Element mit dem Index. Eine Liste oder
 * ein Wert im Patch ersetzt (Operationen über die ganze Liste, `null`).
 */
export function wendeAn(ziel: unknown, patch: unknown): unknown {
  if (!istObjekt(patch)) return patch
  if (Array.isArray(ziel)) {
    const neu = [...ziel]
    for (const [k, v] of Object.entries(patch)) {
      const i = Number(k)
      if (!Number.isInteger(i) || i < 0 || i >= neu.length) continue
      neu[i] = angefasst(wendeAn(neu[i], v), v)
    }
    return neu
  }
  const basis = istObjekt(ziel) ? ziel : {}
  const neu: Record<string, unknown> = { ...basis }
  for (const [k, v] of Object.entries(patch)) neu[k] = wendeAn(basis[k], v)
  return neu
}

/** Der ganze Inhalt aus der Basis der Stelle und einem Patch. */
export function inhaltMit(basis: Datensatz, patch: Datensatz): TagebuchInhalt {
  return tagebuchInhalt(wendeAn(basis, patch))
}

/**
 * „Vorbelegen" (AP-48 4.2–4.4): Belegschaft aus den Buchungen (ersetzt die eigene, wenn gewollt),
 * Lieferungen aus den Wareneingängen dazu (ohne Doppel nach Text), dann Geräte, Fremdfirmen und offene
 * Behinderungen „wie im letzten Bericht". Alles Übernommene ist markiert, bis es angefasst wird.
 */
export function mitVorbelegung(inhalt: TagebuchInhalt, v: TagebuchVorbelegung, belegschaftErsetzen: boolean): TagebuchInhalt {
  let neu: TagebuchInhalt = { ...inhalt, belegschaft: { ...inhalt.belegschaft } }
  if (v.belegschaft && (belegschaftErsetzen || !inhalt.belegschaft.eigene.length)) {
    neu.belegschaft = { ...neu.belegschaft, eigene: v.belegschaft.eigene.map((e) => ({ ...e, vorbelegt: true })), namen: v.belegschaft.namen ?? neu.belegschaft.namen }
  }
  const vorhanden = new Set(inhalt.lieferungen.map((l) => (l.text ?? '').trim()))
  neu = { ...neu, lieferungen: [...inhalt.lieferungen, ...v.lieferungen.filter((l) => !vorhanden.has((l.text ?? '').trim())).map((l) => ({ ...l, vorbelegt: true }))] }
  return tagebuchAusLetztem(neu, v.letzter)
}

/** Zahl aus dem Fachteil (`"15.50"`) für die Anzeige: `15,5`. */
export const zahlAnzeige = (w: string | number | null | undefined): string => {
  if (w === null || w === undefined || w === '') return ''
  const n = Number(w)
  return Number.isFinite(n) ? String(Math.round(n * 100) / 100).replace('.', ',') : String(w)
}
